import React from 'react';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Checkbox from '@amzn/meridian/checkbox';
import Text from '@amzn/meridian/text';
import { AssignmentAssociate, Workstation } from '../models';
import { getInitials } from '../../utils';

export interface WorkstationCardProps {
  workstation: Workstation;
  onAssociateRemove: (associateId: string, workstationId: string) => void;
  onAddAssociate: (workstationId: string) => void;
}

/**
 * Card representing a single workstation within a direct process path
 * (e.g. "Primary line 1"). Lists every associate currently assigned to the
 * workstation with a checkbox, avatar initials, and name. Unchecking an
 * associate's checkbox invokes `onAssociateRemove` so the page can move
 * them back to the unassigned pool. An "Add associate" button at the
 * bottom invokes `onAddAssociate` to let the user pick from the sidebar.
 *
 * Pure presentational: all state (assigned list membership) lives on the
 * parent and is passed in via the `workstation` prop.
 */
const WorkstationCard: React.FC<WorkstationCardProps> = ({
  workstation,
  onAssociateRemove,
  onAddAssociate,
}) => {
  const handleAssociateChange = (associate: AssignmentAssociate, nextChecked: boolean) => {
    // Only react on the checked → unchecked transition; re-checking an
    // already-assigned associate is a no-op here because they're already
    // in this workstation's list.
    if (!nextChecked) {
      onAssociateRemove(associate.associateId, workstation.workstationId);
    }
  };

  const handleAddAssociateClick = () => {
    onAddAssociate(workstation.workstationId);
  };

  return (
    <Box className="assignments-workstation-card" spacingInset="300">
      <Box className="assignments-workstation-card__header">
        <Text type="h100">{workstation.workstationName}</Text>
      </Box>

      <Box className="assignments-workstation-card__associates">
        {workstation.assignedAssociates.map(associate => {
          const initials = getInitials(associate.associateName);
          return (
            <Box
              key={associate.associateId}
              className="assignments-workstation-card__associate"
              spacingInset="200"
            >
              <Checkbox
                checked={true}
                onChange={(nextChecked: boolean) => handleAssociateChange(associate, nextChecked)}
              >
                <Box className="assignments-workstation-card__avatar">
                  <Text type="b200">{initials}</Text>
                </Box>
                <Text type="b300">{associate.associateName}</Text>
              </Checkbox>
            </Box>
          );
        })}
      </Box>

      <Box className="assignments-workstation-card__footer">
        <Button type="tertiary" onClick={handleAddAssociateClick}>
          Add associate
        </Button>
      </Box>
    </Box>
  );
};

export default WorkstationCard;
